import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { type DropResult } from 'react-beautiful-dnd'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { type ActionItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { BillingApi } from 'entities/Billing/api/billing'
import { AddPaymentMethodStore } from 'entities/Billing/ui/AddPaymentMethodModal'
import { AddBankAccountStore } from 'entities/Billing/ui/AddBankAccountModal'
import { AutoRechargeWidgetStore } from 'entities/Billing/ui/AutoRechargeModal'
import { type Source } from 'entities/Billing/model/Source'
import { BillingAddressStore } from 'pages/settings/pages/billing/pages/overview/ui/PaymentMethods/ui/BillingAddressContent/store/billingAddressStore'
import { BillingAddressContent } from 'pages/settings/pages/billing/pages/overview/ui/PaymentMethods/ui/BillingAddressContent/BillingAddressContent'
import { PaymentMethodRow } from 'pages/settings/pages/billing/pages/overview/ui/PaymentMethods/model/PaymentMethodRow'

import styles from './styles.module.scss'

export class PaymentMethodsStore {
  private _isLoading = true
  private _deleteModalId = 'deleteCardModal'
  private _billingAddressModalId = 'billingAddressModal'
  private _tableStore: TableStore<PaymentMethodRow>
  private _billingAddressStore: BillingAddressStore | null = null
  private _disposeReaction?: () => void
  private _disposeSourcesReaction?: () => void
  private _orderedSourceIds: string[] = []

  constructor(private _entities: IEntitiesStore) {
    this._tableStore = new TableStore<PaymentMethodRow>({
      key: 'paymentMethodsTable',
    })
    makeAutoObservable(this)

    this._initSourcesReaction()
    this.init()
  }

  private _initSourcesReaction = () => {
    this._disposeSourcesReaction?.()
    this._disposeSourcesReaction = reaction(
      () => this._entities.billingStore.sources.map((s) => s.id),
      (sourceIds) => {
        const currentIds = new Set(this._orderedSourceIds)
        const newIds = sourceIds.filter((id) => !currentIds.has(id))
        const validIds = this._orderedSourceIds.filter((id) => sourceIds.includes(id))

        if (newIds.length > 0 || validIds.length !== this._orderedSourceIds.length) {
          runInAction(() => {
            this._orderedSourceIds = this._sortSources(this._entities.billingStore.sources).map(
              (s) => s.id
            )
          })
        }
      }
    )
  }

  init = async () => {
    try {
      await Promise.all([
        this._entities.billingStore.fetchBillingSources(),
        this._entities.billingStore.fetchAutorecharge(),
        this._entities.billingStore.fetchBillingAddress(),
      ])
    } finally {
      runInAction(() => {
        this._isLoading = false
        this._orderedSourceIds = this._sortSources(this._entities.billingStore.sources).map(
          (s) => s.id
        )
      })
    }
  }

  get isLoading() {
    return this._isLoading
  }

  get sources() {
    return this._entities.billingStore.sources
  }

  get achFlowEnabled() {
    return this._entities.organizationStore.isAchEnabled
  }

  private _sortSources(sources: Source[]): Source[] {
    return [...sources].sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1
      if (!a.isPrimary && b.isPrimary) return 1

      if (a.isMethodCard && b.isMethodAch) return -1
      if (a.isMethodAch && b.isMethodCard) return 1

      if (a.isMethodCard && b.isMethodCard) {
        if (a.priority !== null && b.priority === null) return -1
        if (a.priority === null && b.priority !== null) return 1

        if (a.priority !== null && b.priority !== null) {
          return a.priority - b.priority
        }
      }

      return 0
    })
  }

  get orderedSources() {
    if (!this._orderedSourceIds.length) {
      return this._sortSources(this.sources)
    }

    return this._orderedSourceIds
      .map((id) => this.sources.find((s) => s.id === id))
      .filter((s): s is Source => Boolean(s))
  }

  get primarySource() {
    return this.sources.find((source) => source.isPrimary)
  }

  get autorechargeSourceId() {
    return this._entities.billingStore.autorecharge?.source ?? null
  }

  get tableStore() {
    return this._tableStore
  }

  get paymentMethodRows(): PaymentMethodRow[] {
    return this.orderedSources.map((source) => {
      const row = new PaymentMethodRow({
        id: source.id,
        source,
        isAutoRecharge:
          this.isAutoRechargeSource(source.id) &&
          (this._entities.billingStore.autorecharge?.is_active ?? false),
      })

      if (!source.isPrimary && source.brand) {
        row.setActionsProps({
          actions: this.getRowActions(source),
        })
      }

      if (source.isMethodAch) {
        row.setActionsProps({
          actions: this.getAchRowActions(source),
        })
      }

      return row
    })
  }

  get draggableRows() {
    return this.paymentMethodRows.filter((row, index) => index !== 0 && row.source.brand)
  }

  onDragEnd = async (result: DropResult) => {
    if (!result.destination) return

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index

    if (sourceIndex === destinationIndex) return

    const draggableIds = this.draggableRows.map((row) => row.id)

    const draggedId = draggableIds[sourceIndex]
    const targetId = draggableIds[destinationIndex]

    const actualSourceIndex = this._orderedSourceIds.indexOf(draggedId)
    const actualDestinationIndex = this._orderedSourceIds.indexOf(targetId)

    if (actualSourceIndex === -1 || actualDestinationIndex === -1) return

    const previousOrderedIds = [...this._orderedSourceIds]
    const newOrderedIds = [...this._orderedSourceIds]
    const [removed] = newOrderedIds.splice(actualSourceIndex, 1)
    newOrderedIds.splice(actualDestinationIndex, 0, removed)

    runInAction(() => {
      this._orderedSourceIds = newOrderedIds
    })

    try {
      const cardIds = newOrderedIds.filter((id) => {
        const source = this.sources.find((s) => s.id === id)
        return source?.brand
      })

      const response = await BillingApi.updateSourcesPriority({
        sources: cardIds,
        primary_source: this.primarySource?.id ?? '',
      })

      if (response.status !== 200) {
        runInAction(() => {
          this._orderedSourceIds = previousOrderedIds
        })
      }
    } catch (e) {
      logger.error(e)
      runInAction(() => {
        this._orderedSourceIds = previousOrderedIds
      })
    }
  }

  isAutoRechargeSource = (sourceId: string) => {
    return this.autorechargeSourceId === sourceId
  }

  private _refreshSources = () => {
    runInAction(() => {
      this._orderedSourceIds = this._sortSources(this._entities.billingStore.sources).map(
        (s) => s.id
      )
    })
  }

  onAddPaymentMethodClick = () => {
    new AddPaymentMethodStore(() => {
      this._refreshSources()
    }, this._entities.billingStore)
  }

  onAddBankAccountClick = () => {
    new AddBankAccountStore(async () => {
      await this._entities.billingStore.fetchBillingSources()
      this._refreshSources()
    })
  }

  onBillingAddressClick = async () => {
    this._billingAddressStore = new BillingAddressStore(this._entities)
    this._billingAddressStore.init()

    modalStore.addModal({
      id: this._billingAddressModalId,
      title: 'Billing address',
      width: 480,
      paddingContent: '0px 24px 16px 24px',
      ModalContent: BillingAddressContent,
      ModalContentProps: {
        store: this._billingAddressStore,
      },
      primaryAction: {
        text: 'Save',
        onAction: this._onSaveBillingAddress,
        disabled: !this._billingAddressStore.isAddressFilled,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: this._closeBillingAddressModal,
      },
    })

    this._disposeReaction?.()
    this._disposeReaction = reaction(
      () => ({
        isAddressFilled: this._billingAddressStore?.isAddressFilled,
        saving: this._billingAddressStore?.saving,
      }),
      ({ isAddressFilled, saving }) => {
        const modal = modalStore.getModal(this._billingAddressModalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modal.primaryAction.disabled = !isAddressFilled || saving
        }
      }
    )
  }

  private _closeBillingAddressModal = () => {
    this._disposeReaction?.()
    this._billingAddressStore = null
    modalStore.removeModal(this._billingAddressModalId)
  }

  private _onSaveBillingAddress = async () => {
    if (!this._billingAddressStore) return

    const success = await this._billingAddressStore.save()
    if (success) {
      this._closeBillingAddressModal()
    }
  }

  onSetPrimaryClick = async (sourceId: string) => {
    const success = await this._entities.billingStore.setSourceAsPrimary(sourceId)

    if (success) {
      runInAction(() => {
        this.sources.forEach((source) => {
          source.setPrimary(source.id === sourceId)
        })
        this._orderedSourceIds = this._sortSources(this._entities.billingStore.sources).map(
          (s) => s.id
        )
      })
    }
  }

  onDeleteClick = (source: Source) => {
    const isAutoRechargeCard =
      this.isAutoRechargeSource(source.id) &&
      (this._entities.billingStore.autorecharge?.is_active ?? false)

    if (isAutoRechargeCard) {
      modalStore.addModal({
        id: this._deleteModalId,
        type: ModalTypeList.FAIL,
        title: 'This card is used for Auto-Recharge',
        subtitle: source.name,
        desc: 'To delete this card, choose a different card for Auto-Recharge or turn it off.',
        showCloseButton: false,
        width: 280,
        primaryAction: {
          text: 'Choose different card',
          onAction: () => {
            modalStore.removeModal(this._deleteModalId)
            const autoRechargeStore = new AutoRechargeWidgetStore(
              this._entities.billingStore,
              this._entities.subscriptionStore
            )
            autoRechargeStore.openModal()
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => {
            modalStore.removeModal(this._deleteModalId)
          },
        },
      })
      return
    }

    modalStore.addModal({
      id: this._deleteModalId,
      type: ModalTypeList.ALERT,
      title: 'Delete card?',
      subtitle: source.name,
      desc: 'This action cannot be undone',
      showCloseButton: false,
      width: 280,
      primaryAction: {
        text: 'Yes, delete',
        onAction: async () => {
          await this._entities.billingStore.deleteSource(source.id)
          modalStore.removeModal(this._deleteModalId)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.removeModal(this._deleteModalId)
        },
      },
    })
  }

  onDeleteAchClick = (source: Source) => {
    const isPrimaryACH = this._entities.subscriptionStore.defaultPaymentMethodId === source.id

    modalStore.addModal({
      id: this._deleteModalId,
      type: ModalTypeList.ALERT,
      title: 'Delete ACH?',
      subtitle: source.name,
      desc: isPrimaryACH
        ? 'This action cannot be undone. Subscription charges will move to your primary payment method.'
        : 'This action cannot be undone',
      showCloseButton: false,
      width: 320,
      primaryAction: {
        text: 'Yes, delete',
        onAction: async () => {
          await this._entities.billingStore.deleteAchSource(source.id)
          modalStore.removeModal(this._deleteModalId)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.removeModal(this._deleteModalId)
        },
      },
    })
  }

  getRowActions = (source: Source): ActionItem[] => {
    return [
      {
        icon: 'card',
        text: 'Set as primary',
        onAction: () => this.onSetPrimaryClick(source.id),
        buttonProps: {
          style: { width: 185, cursor: 'pointer' },
          iconRightProps: {
            icon: 'question',
            fontSize: 14,
            color: 'var(--content-neutral-primary)',
          },
          iconRightTooltipProps: {
            className: styles.setPrimaryTooltip,
            label: 'Use this card as your main payment method for all transactions',
            placement: 'top',
            width: '238px',
          },
        },
      },
      {
        icon: 'delete',
        text: 'Delete',
        onAction: () => this.onDeleteClick(source),
      },
    ]
  }

  getAchRowActions = (source: Source): ActionItem[] => {
    return [
      {
        icon: 'delete',
        text: 'Delete',
        onAction: () => this.onDeleteAchClick(source),
      },
    ]
  }

  dispose = () => {
    this._disposeReaction?.()
    this._disposeSourcesReaction?.()
  }
}
